import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Phone, BookOpen, FileText } from 'lucide-react';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';

import { t } from '@/i18n';
const PublicNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '/', label: t("Home") },
    { href: '/florida-noc', label: t("Florida NOC"), highlight: true },
    { href: '/services', label: t("Services") },
    { href: '/pricing', label: t("Pricing") },
    { href: '/learning-center', label: t("50 State Guide"), icon: BookOpen },
    { href: '/blog', label: t("Blog"), icon: FileText },
    { href: '/faq', label: 'FAQ' },
    { href: '/about', label: t("About") },
    { href: '/contact', label: t("Contact") },
  ];

  const isActive = (href) => {
    if (href === '/learning-center') return location.pathname.startsWith('/learning-center');
    if (href === '/blog') return location.pathname.startsWith('/blog');
    return location.pathname === href;
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-sm" data-testid="public-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-6 h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt={t("All American Notice")} 
              className="h-12 lg:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4 text-sm min-w-0">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                  link.highlight
                    ? 'text-white font-bold bg-[#F97316] hover:bg-[#ea6a10] px-3 py-1.5 rounded-full'
                    : isActive(link.href)
                    ? 'text-[#F97316]'
                    : 'text-slate-600 hover:text-[#1e3a5f]'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.icon && <link.icon className="h-4 w-4" />}
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a href="tel:561-680-2857" className="hidden 2xl:flex items-center gap-2 text-[#1e3a5f] font-medium whitespace-nowrap">
              <Phone className="h-4 w-4" />
              <span>561-680-2857</span>
            </a>
            <LanguageSwitcher />
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')} 
              className="text-[#1e3a5f] hover:text-[#1e3a5f] hover:bg-slate-100"
              data-testid="nav-login-btn"
            >
              {t("Login")}
            </Button>
            <Button 
              onClick={() => navigate('/register')} 
              className="bg-[#F97316] hover:bg-[#ea6a10] text-white font-semibold px-6"
              data-testid="nav-get-started-btn"
            >
              {t("Get Started")}
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium ${
                        link.highlight ? 'text-white font-bold bg-[#F97316] rounded-full px-4 py-2 self-start'
                          : isActive(link.href) ? 'text-[#F97316]' : 'text-slate-700'
                      }`}
                      data-testid={`mobile-nav-link-${link.href.replace(/\//g, '') || 'home'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <hr className="border-slate-200" />
                  <a href="tel:561-680-2857" className="flex items-center gap-2 text-[#1e3a5f] font-medium whitespace-nowrap">
                    <Phone className="h-4 w-4" />
                    <span>561-680-2857</span>
                  </a>
                  <Button 
                    variant="outline" 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full"
                  >
                    {t("Login")}
                  </Button>
                  <Button 
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    className="w-full bg-[#F97316] hover:bg-[#ea6a10]"
                  >
                    {t("Get Started")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PublicNavbar;
